import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, ChevronRight, User, FolderKanban, CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/utils';

export default function HighPriorityTasksWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHighPriorityTasks();
  }, [user]);

  const fetchHighPriorityTasks = async () => {
    try {
      // Fetch tasks assigned to current user with high priority
      const data = await apiRequest('/projects/my-high-priority-tasks');
      setTasks(data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch high priority tasks:', error);
      // Fallback to empty array
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDueBadge = (dueDate) => {
    const days = getDaysUntilDue(dueDate);
    if (days === null) return { text: 'No due date', color: 'bg-slate-100 text-slate-600' };
    if (days < 0) return { text: 'Overdue', color: 'bg-red-100 text-red-700' };
    if (days === 0) return { text: 'Due Today', color: 'bg-red-100 text-red-700' };
    if (days === 1) return { text: 'Due Tomorrow', color: 'bg-orange-100 text-orange-700' };
    if (days <= 3) return { text: `${days} days`, color: 'bg-yellow-100 text-yellow-700' };
    return { text: `${days} days`, color: 'bg-green-100 text-green-700' };
  };

  return (
    <Card className="h-full border-red-200 bg-red-50/30" data-testid="high-priority-tasks-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            High Priority Tasks
            {tasks.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 rounded-full">
                {tasks.length}
              </span>
            )}
          </CardTitle>
          {expandable && expandPath && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(expandPath)}
              className="text-slate-500 hover:text-slate-700"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-red-100/50 rounded-lg"></div>
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-green-500" />
            <p className="text-sm text-slate-600 font-medium">No high priority tasks!</p>
            <p className="text-xs text-slate-400">You're all caught up</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {tasks.map((task) => {
              const dueBadge = getDueBadge(task.due_date);
              return (
                <div 
                  key={task.id || task._id}
                  onClick={() => navigate(`/projects/${task.project_id}`)}
                  className="p-3 rounded-lg border border-red-200 bg-white hover:bg-red-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-800 truncate">{task.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <FolderKanban className="h-3 w-3 text-slate-400" />
                        <span className="text-xs text-slate-500 truncate">{task.project_name}</span>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${dueBadge.color}`}>
                      {dueBadge.text}
                    </span>
                  </div>
                  {task.description && (
                    <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{task.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
